import { Status as CustomerStatus } from '@brighterly/lib-core-types/customer/enums'

import { useCustomerStore } from '@entities/customer'

import { LessonsActionModal, LessonsDeleteModal } from './enums'
import { useLessonsStore } from './store'
import { useLessonPrivileges } from './lesson-privileges.hook'
import { isReservedLesson } from './lessons'

interface LessonsControlsReturn {
  refIsShown: ComputedRef<boolean>
  refHasSelection: ComputedRef<boolean>
  refSelectedCount: ComputedRef<number>
  refHasReservedSelected: ComputedRef<boolean>
  refIsSelectionMixed: ComputedRef<boolean>
  refCanDelete: ComputedRef<boolean>
  refIsDeal: ComputedRef<boolean>
  refHasReservedLessons: ComputedRef<boolean>
  openDeleteSelected: () => void
  openDeleteReserved: () => void
  openBulkEdit: () => void
}

/** The buttons right of the filters: the Add lesson menu and Cancel reserved, or the bulk edit and delete once rows
 *  are ticked. */
export const useLessonsControls = (): LessonsControlsReturn => {
  const store = useLessonsStore()
  const customerStore = useCustomerStore()
  const { canManageBookings, canDeleteBookings } = useLessonPrivileges()

  /** The legacy tab showed these controls to lesson managers only. */
  const refIsShown = computed<boolean>(() => canManageBookings.value)

  const refHasSelection = computed<boolean>(() => store.hasSelection)

  const refSelectedCount = computed<number>(() => store.getSelectedIds.length)

  const refHasReservedSelected = computed<boolean>(() => store.hasReservedSelected)

  const refCanDelete = computed<boolean>(() => canDeleteBookings.value)

  /** Reserved and other lessons cannot be edited together: the API refuses the mix. */
  const refIsSelectionMixed = computed<boolean>(() => {
    const selected = store.getLessons.filter(lesson => store.getSelectedIds.includes(lesson.id))
    const reservedCount = selected.filter(lesson => isReservedLesson(lesson.status)).length

    return reservedCount > 0 && reservedCount < selected.length
  })

  const refIsDeal = computed<boolean>(() => customerStore.getCustomer?.status === CustomerStatus.Deal)

  const refHasReservedLessons = computed<boolean>(() => store.getReservedLessons.length > 0)

  const openDeleteSelected = (): void => {
    store.openDeleteModal(LessonsDeleteModal.Selected)
  }

  const openDeleteReserved = (): void => {
    store.openDeleteModal(LessonsDeleteModal.Reserved)
  }

  const openBulkEdit = (): void => {
    store.openActionModal(LessonsActionModal.BulkEdit)
  }

  return {
    refIsShown,
    refHasSelection,
    refSelectedCount,
    refHasReservedSelected,
    refIsSelectionMixed,
    refCanDelete,
    refIsDeal,
    refHasReservedLessons,
    openDeleteSelected,
    openDeleteReserved,
    openBulkEdit,
  }
}
